import type { StyleLibrary } from "schemas/generated/project";

import React, { createContext } from "react";

import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

interface SandboxStateStore {
  viewingVersionId: string | null;
  currentStyleLibrary: StyleLibrary | null;

  actions: {
    setViewingVersionId: (viewingVersionId: string | null) => void;
    setCurrentStyleLibrary: (currentStyleLibrary: StyleLibrary | null) => void;
  };
}

const createSandboxStateStore = () => {
  return createStore<SandboxStateStore>((set) => ({
    viewingVersionId: null,
    currentStyleLibrary: null,
    actions: {
      setViewingVersionId: (viewingVersionId) => set({ viewingVersionId }),
      setCurrentStyleLibrary: (currentStyleLibrary) => {
        return set({ currentStyleLibrary });
      },
    },
  }));
};

type SandboxStateContextType = {
  store: ReturnType<typeof createSandboxStateStore>;
};

const SandboxStateContext = createContext<SandboxStateContextType | null>(null);

export const useSandboxStateContext = () => {
  return useRequiredContext(SandboxStateContext);
};

type SandboxStateProviderProps = React.PropsWithChildren;

// NOTE (Bobby, 2025-10-07): This provider is used to track the current sandbox state. The user may be viewing a temporary version of the sandbox so we
// need to make sure that temporary state is not overridden when we call the getPreviewUrl endpoint again. We call that endpoint continuously in order to keep
// the sandbox connected and we need to make sure to track these values so when we call getPreviewUrl we can tell the backend what the current state of the
// sandbox is and make sure we persist that state.
export function SandboxStateProvider({ children }: SandboxStateProviderProps) {
  const [store] = React.useState(() => createSandboxStateStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <SandboxStateContext.Provider value={contextValue}>
      {children}
    </SandboxStateContext.Provider>
  );
}

/**
 * Hook which accesses part of the synced sandbox store.
 *
 * IMPORTANT: This hook should not be used for UI state. This is the global state of the sandbox -
 * if you want to have UI state propagated to the sandbox, use `useSyncedSandboxStateUrl`
 */
export function useSandboxStateStore<SelectorReturnType>(
  selector: (state: SandboxStateStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useSandboxStateContext();
  return useShallowStore(store, selector);
}

const useSandboxStateActions = () => {
  return useSandboxStateStore((state) => state.actions);
};

/**
 * Hook which gives you a url to use for a nextjs dev server sandbox iframe.
 *
 * This hook takes care of syncing the appropriate arguments to the sandbox so that the
 * url returned has the correct version id, style library overrides, etc. This should be
 * the ONLY way our editor accesses the sandbox URL to display it - if we access the URL
 * another way, we risk the state where the sandbox happens to be polluted with other
 * state which was supposed to be temporary.
 *
 * @param opts.overrideStyleLibrary The style library to override the sandbox with - if
 * provided, design library colors will show as if this style library was the current one
 * @param opts.viewingVersionId The version id to view in the sandbox - this will force
 * certain pages to render as if their current version was this one
 * @param opts.forceSkip Whether to force skip the sync - if true none of the params to this
 * hook will actually sync and you'll just get back the default sandbox url. Useful if you
 * don't need a sandbox url because you might just be displaying a cached static page.
 * @returns The url to use for the sandbox iframe.
 */
export const useSyncedSandboxStateUrl = (opts: {
  overrideStyleLibrary: StyleLibrary | null;
  viewingVersionId: string | null;
  forceSkip?: boolean;
}) => {
  const { overrideStyleLibrary, viewingVersionId, forceSkip } = opts;
  const { url } = useNextDevServer();
  const actions = useSandboxStateActions();

  // Note (Noah, 2025-10-29): Any time our overridden style library changes, we
  // should sync an update to the sandbox state
  React.useEffect(() => {
    if (forceSkip) {
      return;
    }
    actions.setCurrentStyleLibrary(overrideStyleLibrary);
    return () => {
      actions.setCurrentStyleLibrary(null);
    };
  }, [overrideStyleLibrary, actions, forceSkip]);

  // Note (Noah, 2025-10-29): Any time our viewing version id changes, we should
  // sync an update to the sandbox state
  React.useEffect(() => {
    if (forceSkip) {
      return;
    }
    actions.setViewingVersionId(viewingVersionId);
    return () => {
      actions.setViewingVersionId(null);
    };
  }, [viewingVersionId, actions, forceSkip]);
  return url;
};
